"use client";

import { useState } from "react";
import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import { inputLines } from "@/lib/tool-kit/identifiers/identifier-input";
import {
  analyzeIsbn,
  type CheckDigitWorking,
  type IsbnAnalysis,
  isbn10Working,
  isbn13Working,
} from "@/lib/tool-kit/identifiers/isbn";

/** English strings. A translated content file overrides these through `ui`. */
export const ISBN_CONVERTER_STRINGS = {
  inputLabel: "ISBNs — one per line",
  inputPlaceholder: "978-0-306-40615-7\n0-8044-2957-X\n979-10-90636-07-1",
  inputHint:
    "Hyphens, spaces and an ISBN label are ignored. Results are written without hyphens — placing them needs the ISBN Agency's range tables, which this tool does not apply.",
  emptyPrompt: "Paste an ISBN-10 or ISBN-13 to convert and check it.",
  results: "Results",
  validCount: "Valid",
  invalidCount: "Not valid",
  no10Count: "979 — no ISBN-10",
  valid10: "Valid ISBN-10",
  valid13: "Valid ISBN-13",
  invalid: "Not a valid ISBN",
  isbn10: "ISBN-10",
  isbn13: "ISBN-13",
  none: "—",
  no10: "This is a 979 ISBN. The 979 prefix was never used for ISBN-10s, so there is no ten-digit form.",
  badLength: "An ISBN has 10 or 13 digits; this line has {count}.",
  badCharacters:
    "Only digits, hyphens, spaces and a final X belong in an ISBN.",
  badX: "X can only be the check digit of an ISBN-10 — the last character.",
  badPrefix:
    "Every ISBN-13 starts with 978 or 979; {prefix} is another kind of EAN-13 barcode number.",
  badCheck:
    "The check digit should be {expected}, not {actual}. A digit is mistyped somewhere — the check digit cannot say which.",
  showWorking: "Show the check-digit working",
  checkingTitle: "Checking the {kind} check digit",
  computingTitle: "Computing the {kind} check digit",
  digitRow: "Digit",
  weightRow: "Weight",
  productRow: "Product",
  workingSum: "Sum of the products: {sum}",
  workingRemainder: "{sum} mod {modulus} = {remainder}",
  workingCheck:
    "Check digit: ({modulus} − {remainder}) mod {modulus} = {value}",
  workingX: "A check value of 10 is written X.",
  copy13: "Copy ISBN-13s",
  copy10: "Copy ISBN-10s",
  copied: "Copied!",
  downloadCsv: "Download CSV",
  csvHeader: "input,valid,isbn10,isbn13",
  csvYes: "yes",
  csvNo: "no",
  tooMany: "Only the first {max} lines are shown.",
} as const;

type Strings = typeof ISBN_CONVERTER_STRINGS;

const MAX_LINES = 1000;
const RE_CSV_QUOTE = /"/g;

function csvCell(value: string): string {
  return `"${value.replace(RE_CSV_QUOTE, '""')}"`;
}

function problemText(analysis: IsbnAnalysis, strings: Strings): string {
  switch (analysis.problem) {
    case "length":
      return t(strings.badLength, { count: analysis.compact.length });
    case "x-position":
      return strings.badX;
    case "prefix":
      return t(strings.badPrefix, { prefix: analysis.compact.slice(0, 3) });
    case "check-digit":
      return t(strings.badCheck, {
        expected: analysis.expectedCheck ?? "",
        actual: analysis.compact.at(-1) ?? "",
      });
    default:
      return strings.badCharacters;
  }
}

function WorkingTable({
  working,
  title,
  strings,
}: {
  working: CheckDigitWorking;
  title: string;
  strings: Strings;
}) {
  const raw = (working.modulus - working.remainder) % working.modulus;
  const rows: [string, number[]][] = [
    [strings.digitRow, working.digits],
    [strings.weightRow, working.weights],
    [strings.productRow, working.products],
  ];
  return (
    <div className="space-y-2">
      <p className="font-medium text-foreground text-sm">{title}</p>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className={TABLE}>
          <tbody>
            {rows.map(([label, values]) => (
              <tr key={label}>
                <th className={TH} scope="row">
                  {label}
                </th>
                {values.map((value, index) => (
                  // Positional cells: the nth digit, weight or product.
                  // biome-ignore lint/suspicious/noArrayIndexKey: positional cells
                  <td className={`${TD} text-center font-mono`} key={index}>
                    {value}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="font-mono text-foreground text-sm">
        {t(strings.workingSum, { sum: working.sum })}
        <br />
        {t(strings.workingRemainder, {
          sum: working.sum,
          modulus: working.modulus,
          remainder: working.remainder,
        })}
        <br />
        {t(strings.workingCheck, {
          modulus: working.modulus,
          remainder: working.remainder,
          value: raw,
        })}
      </p>
      {working.check === "X" ? (
        <p className="text-muted-foreground text-sm">{strings.workingX}</p>
      ) : null}
    </div>
  );
}

/** The input's own check, then the converted number's. */
function workings(analysis: IsbnAnalysis, strings: Strings) {
  const list: { title: string; working: CheckDigitWorking }[] = [];
  const kindOf = (kind: string) =>
    kind === "isbn10" ? strings.isbn10 : strings.isbn13;
  if (analysis.working && analysis.kind) {
    list.push({
      title: t(strings.checkingTitle, { kind: kindOf(analysis.kind) }),
      working: analysis.working,
    });
  }
  if (analysis.valid && analysis.kind === "isbn10" && analysis.isbn13) {
    list.push({
      title: t(strings.computingTitle, { kind: strings.isbn13 }),
      working: isbn13Working(analysis.isbn13.slice(0, 12)),
    });
  }
  if (analysis.valid && analysis.kind === "isbn13" && analysis.isbn10) {
    list.push({
      title: t(strings.computingTitle, { kind: strings.isbn10 }),
      working: isbn10Working(analysis.isbn10.slice(0, 9)),
    });
  }
  return list;
}

function statusLabel(analysis: IsbnAnalysis, strings: Strings): string {
  if (!analysis.valid) {
    return strings.invalid;
  }
  return analysis.kind === "isbn10" ? strings.valid10 : strings.valid13;
}

function ResultCard({
  analysis,
  strings,
}: {
  analysis: IsbnAnalysis;
  strings: Strings;
}) {
  const steps = workings(analysis, strings);
  const no10 = analysis.valid && !analysis.isbn10;
  return (
    <li className={`${CARD} space-y-3`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="break-all font-mono text-foreground">
          {analysis.input}
        </span>
        <span
          className={
            analysis.valid
              ? "font-medium text-emerald-700 text-sm dark:text-emerald-400"
              : "font-medium text-destructive text-sm"
          }
        >
          {statusLabel(analysis, strings)}
        </span>
      </div>
      {analysis.valid ? (
        <dl className="grid gap-2 font-mono text-sm sm:grid-cols-2">
          <div>
            <dt className="text-muted-foreground text-xs">{strings.isbn13}</dt>
            <dd className="text-foreground">{analysis.isbn13}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">{strings.isbn10}</dt>
            <dd className="text-foreground">
              {analysis.isbn10 ?? strings.none}
            </dd>
          </div>
        </dl>
      ) : (
        <p className="text-muted-foreground text-sm">
          {problemText(analysis, strings)}
        </p>
      )}
      {no10 ? (
        <p className="text-muted-foreground text-sm">{strings.no10}</p>
      ) : null}
      {steps.length > 0 ? (
        <details className="text-sm">
          <summary className="cursor-pointer text-primary">
            {strings.showWorking}
          </summary>
          <div className="mt-3 space-y-4">
            {steps.map((step) => (
              <WorkingTable
                key={step.title}
                strings={strings}
                title={step.title}
                working={step.working}
              />
            ))}
          </div>
        </details>
      ) : null}
    </li>
  );
}

export default function IsbnConverterWidget() {
  const strings = useToolUi(ISBN_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("");

  const lines = inputLines(text);
  const analyses = lines.slice(0, MAX_LINES).map(analyzeIsbn);
  const valid = analyses.filter((analysis) => analysis.valid);
  const isbn13s = valid.map((analysis) => analysis.isbn13 ?? "").join("\n");
  const isbn10s = valid.flatMap((analysis) => analysis.isbn10 ?? []).join("\n");
  const csv = [
    strings.csvHeader,
    ...analyses.map((analysis) =>
      [
        csvCell(analysis.input),
        analysis.valid ? strings.csvYes : strings.csvNo,
        analysis.isbn10 ?? "",
        analysis.isbn13 ?? "",
      ].join(",")
    ),
  ].join("\n");

  return (
    <section className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="isbnc-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        rows={5}
        value={text}
      />
      <div aria-live="polite" className="space-y-4">
        {analyses.length === 0 ? (
          <Notice>{strings.emptyPrompt}</Notice>
        ) : (
          <>
            <h2 className={WIDGET_H2}>{strings.results}</h2>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.validCount}
                value={fmt(valid.length)}
              />
              <StatCard
                label={strings.invalidCount}
                value={fmt(analyses.length - valid.length)}
              />
              <StatCard
                label={strings.no10Count}
                value={fmt(valid.filter((analysis) => !analysis.isbn10).length)}
              />
            </StatGrid>
            <div className="flex flex-wrap gap-3">
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copy13}
                text={isbn13s}
              />
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copy10}
                text={isbn10s}
              />
              <button
                className={BUTTON_SECONDARY}
                onClick={() =>
                  downloadText("isbns.csv", csv, "text/csv;charset=utf-8")
                }
                type="button"
              >
                {strings.downloadCsv}
              </button>
            </div>
            {lines.length > MAX_LINES ? (
              <Notice>{t(strings.tooMany, { max: fmt(MAX_LINES) })}</Notice>
            ) : null}
            <ul className="space-y-3">
              {analyses.map((analysis, index) => (
                <ResultCard
                  analysis={analysis}
                  key={`${index}-${analysis.input}`}
                  strings={strings}
                />
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  );
}
